import { useCallback, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { Coordinates } from 'tripPlanner/utils/chatItinerary'
import useAddHotelToTrip from './useAddHotelToTrip'
import type { SendMessageOptions } from './useAddHotelToTrip'
import type { HotelRoomOption } from 'tripPlanner/types/hotelRoomOption'
import AddHotelToTripModal from './AddHotelToTripModal'
import noop from 'lib/function/noop'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'

interface Props {
  itineraryData?: TripItineraryData
  hotelName: string
  offerId: string
  offerType?: string
  hotelCity?: string
  hotelCoordinates?: Coordinates
  contextStop?: TripStop
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  variant?: 'filled' | 'outline'
  size?: 'small' | 'medium'
  fit?: 'full-width'
  onAfterAction?: () => void
  filterNights?: number
  checkIn?: string
  checkOut?: string
  roomOption?: HotelRoomOption
}

function AddHotelToTripButton({
  itineraryData,
  hotelName,
  offerId,
  offerType,
  hotelCity,
  hotelCoordinates,
  contextStop,
  onSendMessage,
  variant = 'filled',
  size = 'small',
  fit,
  onAfterAction = noop,
  filterNights,
  checkIn,
  checkOut,
  roomOption,
}: Props) {
  const intl = useIntl()
  const { isStreaming } = useChatStreamStatus()
  const {
    handleAddToTrip,
    isModalOpen,
    closeModal,
    itineraryStatus,
    relevantStop,
    defaultDayNumber,
    defaultNights,
    bookingRoomOption,
  } = useAddHotelToTrip({
    itineraryData,
    hotelName,
    offerId,
    offerType,
    hotelCity,
    hotelCoordinates,
    contextStop,
    onSendMessage,
    onAfterAction,
    filterNights,
    checkIn,
    checkOut,
    roomOption,
  })

  const isAdded = itineraryStatus.type !== 'not-added'

  const addToTripLabel = intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)

  const handleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    handleAddToTrip()
  }, [handleAddToTrip])

  const handleModalSubmit = useCallback((message: string) => {
    onSendMessage(message, { offerId, offerType, roomOption: bookingRoomOption })
    closeModal()
    onAfterAction()
  }, [onSendMessage, offerId, offerType, bookingRoomOption, closeModal, onAfterAction])

  const buttonLabel = isAdded ? itineraryStatus.label : addToTripLabel
  const startIcon = !isAdded ? <LinePlusIcon /> : undefined
  const endIcon = isAdded ? <LineAngleDownIcon /> : undefined

  const trigger = fit === 'full-width' ? (
    <TextButton kind="secondary" size={size} fit="full-width" startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isStreaming}>
      {buttonLabel}
    </TextButton>
  ) : (
    <AssistChip variant={variant} size={size} startIcon={startIcon} endIcon={endIcon} onClick={handleClick} disabled={isStreaming}>
      {buttonLabel}
    </AssistChip>
  )

  return (
    <>
      {trigger}
      {itineraryData && (
        <AddHotelToTripModal
          isOpen={isModalOpen}
          hotelName={hotelName}
          itineraryData={itineraryData}
          relevantStop={relevantStop}
          hotelCity={hotelCity}
          defaultDayNumber={defaultDayNumber}
          defaultNights={defaultNights}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
        />
      )}
    </>
  )
}

export default AddHotelToTripButton
